import type { Dispose } from '../ports';

export interface BeforeUnloadEventLike {
  returnValue: string;
  preventDefault(): void;
}

export interface BeforeUnloadTarget {
  addEventListener(
    type: 'beforeunload',
    listener: (event: BeforeUnloadEventLike) => void,
  ): void;
  removeEventListener(
    type: 'beforeunload',
    listener: (event: BeforeUnloadEventLike) => void,
  ): void;
}

export interface BrowserLifecycleInstaller {
  install(isDirty: () => boolean): Dispose;
}

export function installBeforeUnloadGuard(
  target: BeforeUnloadTarget,
  isDirty: () => boolean,
): Dispose {
  const handleBeforeUnload = (event: BeforeUnloadEventLike) => {
    if (!isDirty()) return;

    event.preventDefault();
    event.returnValue = '';
  };

  target.addEventListener('beforeunload', handleBeforeUnload);
  return () => target.removeEventListener('beforeunload', handleBeforeUnload);
}

export function createWindowLifecycleInstaller(
  windowRef: Window,
): BrowserLifecycleInstaller {
  const target: BeforeUnloadTarget = {
    addEventListener: (_type, listener) => {
      windowRef.addEventListener('beforeunload', listener as (event: BeforeUnloadEvent) => void);
    },
    removeEventListener: (_type, listener) => {
      windowRef.removeEventListener(
        'beforeunload',
        listener as (event: BeforeUnloadEvent) => void,
      );
    },
  };

  return {
    install: (isDirty) => installBeforeUnloadGuard(target, isDirty),
  };
}
